import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  FormControl,
  Card,
  Box,
} from "@mui/material";
import {
  StyledButton,
  StyledInputLabel,
  StyledSelect,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledDatePicker,
  OutlinedIconFab,
  StyledMultilineTextField,
  StyledTableContainer,
} from "../StyledComponents";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClearIcon from "@mui/icons-material/Clear";
import { durationType } from "../initialData/initialData";
import {
  getHumanReadableDiffInPastDateWithAgo,
  getHumanReadableDurationInDate,
} from "../Utils/UtilsFunctions";
import Icon from "../Utils/Icons";
import DurationAutocompleteField from "./DurationAutocompleteField";
import {
  durationUnitMap,
  normalizeLegacyDuration,
  parseCombinedDuration,
} from "./durationUtils";

const MedicalHistorySimplified = ({
  medicalHistoryData,
  setMedicalHistoryData,
  activeHistorySection,
  setActiveHistorySection,
  startRecording,
  stopRecording,
  isAiEnabled,
}) => {
  console.log("History of Past Illness called", medicalHistoryData);
  const today = dayjs();
  const [chiefComplaintsStatus, setChiefComplaintsStatus] = useState([
    "Active",
    "Recurrence",
    "Relapse",
    "Inactive",
    "Remission",
    "Resolved",
    "Unknown",
  ]);

  const [medicalHistoryVars, setMedicalHistoryVars] = useState({
    condition: "",
    id: null,
    onset_datetime: null,
    clinical_status_description: "Active",
    abatement_datetime: null,
    clinical_note: "",
    duration: "",
    abatement_duration: "",
  });

  const [conditionName, setConditionName] = useState("");
  const [error, setError] = useState("");
  const [medicalHistoryEditIndex, setMedicalHistoryEditIndex] = useState(null);
  const hasPendingDurationSelection = (durationValue) =>
    /^\d+$/.test(String(durationValue || "").trim());

  console.log("medicalHistoryVars", medicalHistoryVars);

  const handleAddRow = async () => {
    if (
      hasPendingDurationSelection(medicalHistoryVars.duration) ||
      hasPendingDurationSelection(medicalHistoryVars.abatement_duration)
    ) {
      setError("Please select a duration");
      return;
    }
    // console.log("Add Click", medicalHistoryVars);
    if (medicalHistoryVars.condition || conditionName.length > 0) {
      let newDetails = {
        ...medicalHistoryVars,
        id: null,
      };
      const newRow = {
        condition: newDetails.condition,
        id: newDetails.id,
        onset_datetime: newDetails?.onset_datetime?.format("YYYY-MM-DD"),
        clinical_status_description: newDetails.clinical_status_description,
        abatement_datetime: newDetails?.abatement_datetime
          ? newDetails?.abatement_datetime.format("YYYY-MM-DD")
          : null,
        clinical_note: newDetails?.clinical_note,
        duration: normalizeLegacyDuration(
          newDetails?.duration,
          newDetails?.duration,
          newDetails?.chiefComplaintDurationType
        ),
        abatement_duration: normalizeLegacyDuration(
          newDetails?.abatement_duration,
          newDetails?.abatement_duration,
          newDetails?.abatement_duration_type
        ),
      };

      // Update family history rows by adding the new row

      const updatedRows = [...(medicalHistoryData?.new ?? []), newRow];

      setMedicalHistoryData({
        ...(medicalHistoryData ?? { prev: [], new: [] }),
        new: updatedRows,
      });
      // Reset family history variables and clear error
      setMedicalHistoryVars({
        ...medicalHistoryVars,
        condition: "",
        clinical_note: "",
        duration: "",
        abatement_duration: "",
        abatement_datetime: null,
        clinical_status_description: "Active",
      });
      setConditionName("");
      setError("");
    } else {
      // Set error message if any field is missing
      setError("Condition Name field is mandatory");
    }
  };
  const handleMedicalHistoryEditClick = (row, rowIndex) => {
    setConditionName(row.condition);
    setMedicalHistoryVars({
      condition: "",
      id: null,
      onset_datetime: row.onset_datetime ? dayjs(row.onset_datetime) : null,
      abatement_datetime: row?.abatement_datetime
        ? dayjs(row?.abatement_datetime)
        : null,
      clinical_status_description: row?.clinical_status_description,
      clinical_note: row?.clinical_note,
      duration: normalizeLegacyDuration(
        row?.duration,
        row?.duration,
        row?.chiefComplaintDurationType
      ),
      abatement_duration: normalizeLegacyDuration(
        row?.abatement_duration,
        row?.abatement_duration,
        row?.abatement_duration_type
      ),
    });
    setMedicalHistoryEditIndex(rowIndex);
  };
  const handleMedicalHistoryUpdate = async () => {
    if (
      hasPendingDurationSelection(medicalHistoryVars.duration) ||
      hasPendingDurationSelection(medicalHistoryVars.abatement_duration)
    ) {
      setError("Please select a duration");
      return;
    }
    // console.log("Update Click", medicalHistoryVars);
    if (medicalHistoryVars.condition || conditionName.length > 0) {
      let newDetails;

      newDetails = { ...medicalHistoryVars, source: undefined };

      const updatedRow = {
        condition: newDetails?.condition,
        id: newDetails?.id,
        onset_datetime: newDetails?.onset_datetime?.format("YYYY-MM-DD"),
        abatement_datetime:
          newDetails?.abatement_datetime?.format("YYYY-MM-DD"),
        clinical_status_description: newDetails?.clinical_status_description,
        clinical_note: newDetails?.clinical_note,
        duration: normalizeLegacyDuration(
          newDetails?.duration,
          newDetails?.duration,
          newDetails?.chiefComplaintDurationType
        ),
        abatement_duration: normalizeLegacyDuration(
          newDetails?.abatement_duration,
          newDetails?.abatement_duration,
          newDetails?.abatement_duration_type
        ),
        source: undefined,
      };

      // Update family history rows by adding the new row

      const updatedMedicalHistory = (medicalHistoryData?.new ?? []).map(
        (row, index) => {
          if (index === medicalHistoryEditIndex) {
            return updatedRow;
          } else {
            return row;
          }
        }
      );
      setMedicalHistoryData({
        ...(medicalHistoryData ?? { prev: [], new: [] }),
        new: updatedMedicalHistory,
      });
      // Reset family history variables and clear error
      setMedicalHistoryVars({
        ...medicalHistoryVars,
        condition: "",
        clinical_note: "",
        duration: "",
        abatement_duration: "",
        abatement_datetime: null,
        clinical_status_description: "Active",
      });
      setConditionName("");
      setMedicalHistoryEditIndex(null);
      setError("");
    } else {
      // Set error message if any field is missing
      setError("Condition Name field is mandatory");
    }
  };
  const handleMedicalHistoryDelete = (rowIndex) => {
    // Filter out the row at the specified index
    const updatedMedicalHistory = (medicalHistoryData?.new ?? []).filter(
      (row, index) => index !== rowIndex
    );
    // Update the state with the filtered array
    setMedicalHistoryData({
      ...(medicalHistoryData ?? { prev: [], new: [] }),
      new: updatedMedicalHistory,
    });
  };
  const calculateOnsetDate = (duration, type) => {
    if (!duration) return null;
    const today = dayjs(); // Current date
    return today.subtract(duration, durationUnitMap[type]);
  };

  const calculateAbatementDate = (onset_datetime, duration, type) => {
    if (
      duration === null ||
      duration === undefined ||
      isNaN(parseInt(duration)) ||
      !onset_datetime
    )
      return null;
    console.log(
      "date abatement",
      dayjs(onset_datetime).add(duration, durationUnitMap[type])
    );
    return dayjs(onset_datetime).add(duration, durationUnitMap[type]);
  };

  const handleChangeMedicalHistory = (field, value) => {
    let newDetails;
    if (field == "condition") {
      newDetails = {
        ...medicalHistoryVars,
        [field]: value.term,
        id: value.id,
      };
    } else {
      newDetails = {
        ...medicalHistoryVars,
        [field]: value,
      };
    }

    if (field === "duration") {
      const { value: durationValue, unit: durationUnit } = parseCombinedDuration(
        newDetails.duration
      );
      const newOnsetDate =
        durationValue && durationUnit
          ? calculateOnsetDate(parseInt(durationValue, 10), durationUnit)
          : null;
      newDetails = {
        ...newDetails,
        onset_datetime: newOnsetDate,
      };
    }
    if (field === "abatement_duration") {
      const { value: abatementValue, unit: abatementUnit } = parseCombinedDuration(
        newDetails?.abatement_duration
      );

      const newAbatementDate = calculateAbatementDate(
        newDetails?.onset_datetime,
        abatementValue ? parseInt(abatementValue, 10) : null,
        abatementUnit
      );

      newDetails = {
        ...newDetails,
        abatement_datetime: abatementValue ? newAbatementDate : null,
      };
    }

    setMedicalHistoryVars(newDetails);
  };
  const handleClearConditionName = () => {
    setError("");
    setConditionName("");
    setMedicalHistoryVars({
      ...medicalHistoryVars,
      condition: "", // Use computed property name to set the field dynamically
    });
    // setMedicineList([])
  };
  const handleClearClinicalNotes = () => {
    setError("");
    setMedicalHistoryVars({
      ...medicalHistoryVars,
      clinical_note: "", // Use computed property name to set the field dynamically
    });
    // setMedicineList([])
  };

  //   const handleAddOption = async (term) => {
  //     const postBody = {
  //       term: term,
  //       state: "active",
  //       semantictag: "finding++disorder",
  //       acceptability: "synonyms",
  //       returnlimit: "-1",
  //       refsetid: null,
  //       parentid: null,
  //       excludeparentconcept: false,
  //       fullconcept: true,
  //     };

  //     try {
  //       if (conditionName.length > 2) {
  //         const response = await axios.post(
  //           `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
  //           postBody,
  //           { headers: headers }
  //         );
  //         if (response) {
  //           let updatedFound = {},
  //             updatedSelectedOptiondetsils = {},
  //             foundData = false;
  //           console.log("Snomed Response", response);
  //           if (
  //             response.data.status === "Success" &&
  //             response.data.data.length > 0
  //           ) {
  //             foundData = response.data.data.find((d) => d.term === term);
  //             console.log("Found Data", foundData);
  //             if (foundData) {
  //               updatedFound = {
  //                 ...foundData,
  //                 found: true,
  //                 id: foundData.conceptId,
  //               };
  //             } else {
  //               updatedSelectedOptiondetsils = {
  //                 hierarchy: null,
  //                 isPreferredTerm: null,
  //                 conceptState: null,
  //                 conceptFsn: term,
  //                 definitionStatus: null,
  //                 conceptId: null,
  //                 languageCode: "en",
  //                 typeId: null,
  //                 term: term,
  //                 caseSignificanceId: "CASE_INSENSITIVE",
  //                 id: null,
  //                 effectiveTime: null,
  //                 activeStatus: null,
  //                 moduleId: null,
  //               };
  //             }
  //           } else {
  //             updatedSelectedOptiondetsils = {
  //               hierarchy: null,
  //               isPreferredTerm: null,
  //               conceptState: null,
  //               conceptFsn: term,
  //               definitionStatus: null,
  //               conceptId: null,
  //               languageCode: "en",
  //               typeId: null,
  //               term: term,
  //               caseSignificanceId: "CASE_INSENSITIVE",
  //               id: null,
  //               effectiveTime: null,
  //               activeStatus: null,
  //               moduleId: null,
  //             };
  //           }

  //           setAutoCompleteData([]);
  //           setFilteredOptions([]);
  //           setSuggestApiCalled(false);
  //           setDebounceFunctionCalledCondition(true);
  //           if (foundData) {
  //             handleChangeMedicalHistory("condition", updatedFound);
  //           } else {
  //             handleChangeMedicalHistory(
  //               "condition",
  //               updatedSelectedOptiondetsils
  //             );
  //           }
  //         }
  //       }
  //     } catch (err) {
  //       console.log(err);
  //       toast.error(err);
  //     }
  //   };

  const handleConditionTyped = (value) => {
    setConditionName(value);
    handleChangeMedicalHistory("condition", { term: value, id: null });
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ margin: "5px 0px", display: "flex", alignItems: "center" }}
      >
        <Grid item xs={11} sm={11} md={11} sx={{ margin: "5px 0px" }}>
          <StyledTextField
            label="Condition Name"
            variant="outlined"
            value={conditionName}
            onChange={(e) => {
              handleConditionTyped(e.target.value);
            }}
            fullWidth
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearConditionName}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>
                </div>
              ),
            }}
          />
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          <StyledTextField
            // id="outlined-multiline-static"
            variant="outlined"
            label="Clinical Notes"
            // multiline
            // rows={2}
            value={medicalHistoryVars.clinical_note}
            onChange={(e) =>
              handleChangeMedicalHistory("clinical_note", e.target.value)
            }
            fullWidth
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearClinicalNotes}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>
                </div>
              ),
            }}
          />
        </Grid>
        {/* Row: Condition Onset + Condition Duration (matches MedicalHistory.js layout) */}
        <Grid
          item
          xs={11}
          sm={11}
          md={5}
          sx={{ display: "flex", alignItems: "center", gap: 1, pr: { md: 1 } }}
        >
          <Tooltip title="How long ago the condition started">
            <Typography
              component="span"
              variant="body2"
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              Condition Onset:
            </Typography>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 200, md: 220 } }}>
            <DurationAutocompleteField
              label="Duration"
              value={medicalHistoryVars.duration || ""}
              onChange={(combined) =>
                handleChangeMedicalHistory("duration", combined)
              }
              durationType={durationType}
            />
          </Box>
        </Grid>

        <Grid
          item
          xs={11}
          sm={11}
          md={6}
          sx={{ display: "flex", alignItems: "center", gap: 1, pl: { md: 1 } }}
        >
          <Tooltip title="How long the condition has been present">
            <Typography
              component="span"
              variant="body2"
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              Condition Duration:
            </Typography>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 200, md: 220 } }}>
            <DurationAutocompleteField
              label="Duration"
              value={medicalHistoryVars.abatement_duration || ""}
              onChange={(combined) =>
                handleChangeMedicalHistory("abatement_duration", combined)
              }
              durationType={durationType}
            />
          </Box>
        </Grid>

        <Grid item xs={11} md={3} sx={{ display: "none" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <StyledDatePicker
                label="Start Date"
                onChange={(date) =>
                  handleChangeMedicalHistory("onset_datetime", date)
                }
                slotProps={{
                  field: { clearable: true },
                }}
                maxDate={today}
                value={
                  medicalHistoryVars?.onset_datetime
                    ? dayjs(medicalHistoryVars?.onset_datetime)
                    : null
                }
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>

        <Grid item xs={11} sm={11} md={9}>
          <StyledTextField
            select
            value={medicalHistoryVars.clinical_status_description}
            onChange={(e) =>
              handleChangeMedicalHistory(
                "clinical_status_description",
                e.target.value
              )
            }
            placeholder="Select Clinical Status"
            fullWidth
            label="Clinical Status"
          >
            {chiefComplaintsStatus.map((status) => (
              <MenuItem key={status} value={status} sx={{ fontSize: "12px" }}>
                {status}
              </MenuItem>
            ))}
          </StyledTextField>
        </Grid>

        <Grid
          item
          xs={11}
          sm={11}
          md={2}
          sx={{
            margin: "5px 0px",
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
            padding: "0px",
          }}
        >
          <StyledButton
            bgColor="#0E3F66"
            onClick={
              medicalHistoryEditIndex !== null
                ? handleMedicalHistoryUpdate
                : handleAddRow
            }
            variant="contained"
            sx={{ width: { xs: "100%" }, marginLeft: { md: "auto" } }}
            color="defaultColor"
          >
            {medicalHistoryEditIndex !== null ? "Update" : "Add"}
          </StyledButton>
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          {error && <Typography style={{ color: "red" }}>{error}</Typography>}
        </Grid>
        <Grid item xs={11} sm={11}>
          {(medicalHistoryData?.prev ?? []).length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>Condition</StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Clinical Notes
                    </StyledTableCellHistory>

                    <StyledTableCellHistory>
                      Clinical Status
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Condition Onset
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Condition Duration
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {(medicalHistoryData?.prev ?? []).map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      <StyledTableCellHistory>
                        {row.condition}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory sx={{ width: "35%" }}>
                        {row.clinical_note?.length > 0 ? row.clinical_note : "-"}
                      </StyledTableCellHistory>

                      <StyledTableCellHistory>
                        {row?.clinical_status_description ?? "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.onset_datetime
                          ? getHumanReadableDiffInPastDateWithAgo(
                            row?.onset_datetime,
                            row
                          )
                          : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.abatement_datetime
                          ? getHumanReadableDurationInDate(
                            row?.onset_datetime,
                            row?.abatement_datetime,
                            row
                          )
                          : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>-</StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
        <Grid item xs={11} sm={11}>
          {(medicalHistoryData?.new ?? []).length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   margin: "5px 2px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>Condition</StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Clinical Notes
                    </StyledTableCellHistory>

                    <StyledTableCellHistory>
                      Clinical Status
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Condition Onset
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Condition Duration
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {(medicalHistoryData?.new ?? []).map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      {/* {console.log("History of Past Illness row", row)} */}

                      <StyledTableCellHistory>
                        {row?.condition}
                        {row?.source === "voice" && !row.id && (
                          <Tooltip title="Machine-interpreted text — please verify before confirming">
                            <span className="missing-id-warning">⚠️</span>
                          </Tooltip>
                        )}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory sx={{ width: "35%" }}>
                        {row?.clinical_note?.length > 0
                          ? row.clinical_note
                          : "-"}
                      </StyledTableCellHistory>

                      <StyledTableCellHistory>
                        {row?.clinical_status_description ?? "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {normalizeLegacyDuration(
                          row?.duration,
                          row?.duration,
                          row?.chiefComplaintDurationType
                        )
                          ? `${normalizeLegacyDuration(
                            row?.duration,
                            row?.duration,
                            row?.chiefComplaintDurationType
                          )} ago`
                          : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {normalizeLegacyDuration(
                          row?.abatement_duration,
                          row?.abatement_duration,
                          row?.abatement_duration_type
                        ) || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {(medicalHistoryData?.prev ?? []).includes(row) ? (
                          "-"
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <Tooltip title="Edit">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleMedicalHistoryEditClick(row, rowIndex)
                                }
                                sx={{ marginRight: 1 }}
                              >
                                <Icon name="editInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleMedicalHistoryDelete(rowIndex)
                                }
                              >
                                <Icon name="deleteInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>
                        )}
                      </StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default React.memo(MedicalHistorySimplified);
